import { ErrorSection } from '@pickleballinc/features/src/error-pages/error-section';
import Loader from '@pickleballinc/features/src/Loader/Loader';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { useGetPlayerModalMatches, useGetPrevModalMatches } from '@/hooks/matches/hooks';
import {
  MatchPropsShortModalRes,
  PlayerMatchesHistoryInfoModalArgs,
} from '@/modules/results/types';

const defaultPlayerMatchesArgs = {
  playerOneUuid: '',
  playerTwoUuid: '',
  matchType: '',
  formatTitle: '',
  date: '',
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
};

interface HistoryInfoModalProps {
  isOpen?: boolean;
  isSuperAdmin: boolean;
  matchUuid: string;
  shouldFetch?: boolean;
  prevMatchesUuids?: string[] | null;
  playerMatchesArgs?: PlayerMatchesHistoryInfoModalArgs;
  onClose: React.MouseEventHandler;
}

export const HistoryInfoModal = ({
  isOpen,
  onClose,
  isSuperAdmin,
  matchUuid,
  shouldFetch,
  prevMatchesUuids,
  playerMatchesArgs,
}: HistoryInfoModalProps) => {
  const [isDesktop, setIsDesktop] = useState(true);

  const {
    data: prevMatchInfoData,
    isFetching: isFetchingPrev,
    isLoading: isLoadingPrev,
  } = useGetPrevModalMatches(prevMatchesUuids || [], !!isOpen, !!shouldFetch, isSuperAdmin);

  const { playerOneUuid, playerTwoUuid, matchType, formatTitle, date } =
    playerMatchesArgs || defaultPlayerMatchesArgs;

  const {
    data: playerMatchesData,
    isFetching: isFetchingPlayer,
    isLoading: isLoadingPlayer,
  } = useGetPlayerModalMatches(
    playerOneUuid,
    playerTwoUuid,
    matchType,
    formatTitle,
    date,
    !!isOpen,
    isSuperAdmin,
    !shouldFetch
  );

  const matchInfoData = shouldFetch ? prevMatchInfoData : playerMatchesData;

  const isFetching = shouldFetch ? isFetchingPrev : isFetchingPlayer;

  const isLoading = shouldFetch ? isLoadingPrev : isLoadingPlayer;

  let errorMessage = null;
  let pages: MatchPropsShortModalRes[] = [];

  if (!matchInfoData || !matchInfoData.pages) {
    errorMessage = 'Something went wrong!';
  } else if (matchInfoData.pages.length === 0) {
    errorMessage = 'No data found!';
  } else {
    const pageWithError = matchInfoData.pages.find((page) => page.error);
    if (pageWithError) {
      errorMessage = pageWithError.error;
    }
    pages = matchInfoData.pages;
  }
  const selectedMatch = pages
    .flatMap((page) => page.results)
    .find((match) => match.match_uuid === matchUuid);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 767);
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }

    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen]);

  const contentVariants = {
    hidden: { y: '-50%', opacity: 0 },
    visible: {
      y: isDesktop ? '-15%' : '25%',
      opacity: 1,
      transition: { duration: 0.1 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial='hidden'
          animate='visible'
          exit='hidden'
          variants={backdropVariants}
          className='fixed inset-0 z-[60]'
          onClick={onClose}
          aria-labelledby='modal-title'
          role='dialog'
          aria-modal='true'
        >
          <div className='flex max-h-[550px] items-end justify-center px-2 pb-20 pt-4 text-center sm:block sm:h-[700px] sm:p-0'>
            <div className='fixed inset-0 bg-gray-500/75 transition-opacity' aria-hidden='true' />
            <span className='hidden sm:inline-block sm:h-screen sm:align-middle' aria-hidden='true'>
              &#8203;
            </span>
            <motion.div
              initial='hidden'
              animate='visible'
              exit='hidden'
              variants={contentVariants}
              className='inline-block min-w-full overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all sm:min-w-[600px] sm:max-w-lg sm:align-middle'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='relative bg-gray-50 px-4 py-2 text-center sm:px-6'>
                <div className='sm:flex sm:items-start'>
                  <h3
                    className='w-full text-lg font-medium leading-6 text-gray-900'
                    id='modal-title'
                  >
                    Match
                  </h3>
                </div>
                <button
                  type='button'
                  className=' absolute right-3 top-1 cursor-pointer rounded-md px-4 py-1 text-sm font-medium text-gray-700'
                  onClick={onClose}
                >
                  x
                </button>
              </div>
              <div className='h-[500px] grow overflow-y-auto bg-white px-4 pb-4 pt-5 sm:h-[600px] sm:p-6 sm:pb-4'>
                {isFetching || isLoading ? (
                  <Loader isComponent containerClassName='h-full' />
                ) : errorMessage ? (
                  <div className='flex items-center h-full'>
                    <ErrorSection title={errorMessage} />
                  </div>
                ) : selectedMatch ? (
                  <div className='text-xs'>
                    <pre>{JSON.stringify(selectedMatch, null, 2)}</pre>
                  </div>
                ) : (
                  <div className='flex items-center h-full'>
                    <NoDataFound title='Match not found.' />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
